import { createContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService, axiosClient, setAuthContext } from '../api/axiosClient';
import authConfig from '../configs/auth';


// ** Defaults
const defaultProvider = {
  user: null,
  tokens: null,
  loading: true,
  setUser: () => null,
  setTokens: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve()
}

export const AuthContext = createContext(defaultProvider)

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [tokens, setTokens] = useState(defaultProvider.tokens);
  const [loading, setLoading] = useState(defaultProvider.loading)
  const [isInitialized, setIsInitialized] = useState(defaultProvider.isInitialized)

  const getUserData = () => {
    const _userData = window.localStorage.getItem('userData');
    if (_userData !== null) {
      try {
        return JSON.parse(_userData);
      } catch (e) {
        return null
      }
    } else {
      return null;
    }
  }

  useEffect(() => {
    if(tokens) {
      window.localStorage.setItem('accessToken', tokens.accessToken);
      window.localStorage.setItem('refreshToken', tokens.refreshToken);
    }
  }, [tokens]);

  useEffect(() => {
    const initAuth = async () => {
      setIsInitialized(true);

      const storedToken = window.localStorage.getItem('accessToken');
      const userData = getUserData();
      
      if (storedToken && userData) {
        setLoading(true);

        await apiService.get(authConfig.meEndpoint + userData.username)
          .then(async response => {
            setUser({ ...response.data.data });

            setTokens({
              'accessToken': storedToken,
              'refreshToken': window.localStorage.getItem('refreshToken')
            });

            setLoading(false);
          })
          .catch((error) => {
            if (error.response?.status === 401) {
              alert("세션이 종료되었습니다. 다시 로그인하십시오");
              handleLogout();
            } else {
              setLoading(false);
            }
          })
      } else {
        setLoading(false);
      }
    }

    initAuth()
  }, [])

  const handleLogin = (params, errorCallback) => {
    axiosClient
      .post(authConfig.loginEndpoint, params)
      .then(async res => {
        const msg = res.data.data;

        setTokens({
          'accessToken': msg.accessToken,
          'refreshToken': msg.refreshToken
        });

        return msg;
      })
      .then((msg) => {
        // 토큰을 localStorage에 설정한 후 API 호출
        window.localStorage.setItem('accessToken', msg.accessToken);
        window.localStorage.setItem('refreshToken', msg.refreshToken);
        
        apiService
          .get(authConfig.meEndpoint + params.username)
          .then(async response => {     
            setUser({ ...response.data.data })
            window.localStorage.setItem('userData', JSON.stringify(response.data.data))
            
            const returnUrl = searchParams.get("returnUrl") || "";
            const redirectURL = returnUrl && returnUrl !== '/' ? returnUrl : '/';

            navigate(redirectURL, { replace: true });
          })
          .catch(error => {
            if (error.response) {
              if (errorCallback) errorCallback(error.response.data.msg)
            } else {
              console.log('Error', error.message);
            }
          })
      })
      .catch(err => {
        if (errorCallback) errorCallback('비밀번호가 틀렸거나, 현재 사용하지 않는 아이디입니다')
      })
  }

  const handleLogout = () => {
    setUser(null);
    setTokens(null);
    setIsInitialized(false);

    window.localStorage.removeItem('userData');
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('refreshToken');

    navigate('/login');
  }

  const handleRegister = (params, errorCallback) => {
    axiosClient
      .post(authConfig.registerEndpoint, params)
      .then(res => {
        const params = res.data.data

        if (res.data.error) {
          if (errorCallback) errorCallback(res.data.error)
        } else {
          handleLogin({ username: params.username, password: params.password })
        }
      })
      .catch(err => (errorCallback ? errorCallback(err) : null))
  }

  const values = {
    user,
    tokens,
    loading,
    setUser,
    setTokens,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister
  }

  // AuthContext가 변경될 때마다 axiosClient에 전달
  useEffect(() => {
    setAuthContext(values);
  }, [user, tokens, loading, isInitialized]);

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}
